import { supabase } from "@/integrations/supabase/client";

export interface IntegrationAuthConfig {
  id?: string;
  tool: string;
  auth_config_id: string;
  is_enabled: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

class IntegrationConfigService {
  /**
   * Fetch all global integration auth configurations
   */
  async getGlobalConfigs(): Promise<IntegrationAuthConfig[]> {
    const { data, error } = await supabase
      .from("integration_auth_configs_global")
      .select("*")
      .order("tool");

    if (error) {
      console.error("Error fetching integration configs:", error);
      throw error;
    }

    return data || [];
  }

  /**
   * Create or update a global integration auth configuration
   */
  async upsertGlobalConfig(
    tool: string,
    authConfigId: string,
    isEnabled: boolean,
    userId?: string,
  ): Promise<IntegrationAuthConfig> {
    // First, try to find an existing config for this tool
    const { data: existingConfig } = await supabase
      .from("integration_auth_configs_global")
      .select("*")
      .eq("tool", tool)
      .single();

    if (existingConfig) {
      // Update existing config
      const { data, error } = await supabase
        .from("integration_auth_configs_global")
        .update({
          auth_config_id: authConfigId,
          is_enabled: isEnabled,
          updated_by: userId,
          updated_at: new Date().toISOString(),
        })
        .eq("tool", tool)
        .select()
        .single();

      if (error) {
        console.error("Error updating integration config:", error);
        throw error;
      }

      return data;
    } else {
      // Insert new config
      const { data, error } = await supabase
        .from("integration_auth_configs_global")
        .insert({
          tool,
          auth_config_id: authConfigId,
          is_enabled: isEnabled,
          created_by: userId,
          updated_by: userId,
        })
        .select()
        .single();

      if (error) {
        console.error("Error inserting integration config:", error);
        throw error;
      }

      return data;
    }
  }

  /**
   * Toggle the enabled status of a global integration auth configuration
   */
  async toggleGlobalConfig(
    tool: string,
    isEnabled: boolean,
    userId?: string,
  ): Promise<void> {
    const { error } = await supabase
      .from("integration_auth_configs_global")
      .update({
        is_enabled: isEnabled,
        updated_by: userId,
        updated_at: new Date().toISOString(),
      })
      .eq("tool", tool);

    if (error) {
      console.error("Error toggling integration config:", error);
      throw error;
    }
  }

  /**
   * Delete a global integration auth configuration
   */
  async deleteGlobalConfig(tool: string): Promise<void> {
    const { error } = await supabase
      .from("integration_auth_configs_global")
      .delete()
      .eq("tool", tool);

    if (error) {
      console.error("Error deleting integration config:", error);
      throw error;
    }
  }

  /**
   * Check which integrations are configured (have auth configs)
   * Used by tenant UI to show available integrations
   */
  async getConfiguredIntegrations(): Promise<string[]> {
    const { data, error } = await supabase
      .from("integration_auth_configs_global")
      .select("tool")
      .eq("is_enabled", true);

    if (error) {
      console.error("Error fetching configured integrations:", error);
      throw error;
    }

    return (data || []).map((config) => config.tool);
  }

  /**
   * Get integration configurations with auth config IDs for UI
   * Returns both the list of available tools and their auth config IDs
   */
  async getIntegrationConfigs(): Promise<{
    configuredApps: string[];
    authConfigs: Record<string, string>;
  }> {
    try {
      // Fetch global auth configs to get both tool names and auth config IDs
      const globalConfigs = await this.getGlobalConfigs();

      // Filter only enabled configs
      const enabledConfigs = globalConfigs.filter(
        (config) => config.is_enabled,
      );

      // Extract tool names for filtering
      const configuredApps = enabledConfigs.map((config) => config.tool);

      // Create mapping of tool -> auth_config_id
      const authConfigs = enabledConfigs.reduce(
        (acc, config) => {
          acc[config.tool] = config.auth_config_id;
          return acc;
        },
        {} as Record<string, string>,
      );

      console.log("✅ Loaded integration configs:", {
        configuredApps,
        authConfigs,
      });

      return { configuredApps, authConfigs };
    } catch (error) {
      console.error("Error loading integration configs:", error);
      throw error;
    }
  }

  /**
   * Get auth config ID for a specific tool
   */
  async getAuthConfigId(tool: string): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from("integration_auth_configs_global")
        .select("auth_config_id")
        .eq("tool", tool)
        .eq("is_enabled", true)
        .single();

      if (error) {
        console.error(`Error fetching auth config for ${tool}:`, error);
        return null;
      }

      return data?.auth_config_id || null;
    } catch (error) {
      console.error(`Error fetching auth config for ${tool}:`, error);
      return null;
    }
  }

  /**
   * Get organization-specific integration connections
   */
  async getOrganizationConnections(organizationId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from("integration_connections")
      .select("*")
      .eq("organization_id", organizationId)
      .eq("is_active", true);

    if (error) {
      console.error("Error fetching organization connections:", error);
      throw error;
    }

    return data || [];
  }

  /**
   * Delete an integration connection
   */
  async deleteConnection(connectionId: string): Promise<void> {
    const { error } = await supabase
      .from("integration_connections")
      .delete()
      .eq("id", connectionId);

    if (error) {
      console.error("Error deleting connection:", error);
      throw error;
    }
  }

  /**
   * Fetch available tools for a specific integration connection
   */
  async getConnectionTools(
    organizationId: string,
    tool: string,
  ): Promise<any[]> {
    try {
      const response = await fetch("/api/composio/tools", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          organizationId,
          tool: tool.toUpperCase(), // Composio uses uppercase tool names
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tools: ${response.statusText}`);
      }

      const data = await response.json();
      return data.tools || [];
    } catch (error) {
      console.error("Error fetching connection tools:", error);
      throw error;
    }
  }
}

export const integrationConfigService = new IntegrationConfigService();
